import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { useTheme } from 'styled-components';

import { Loader } from '../../Elements/Loader/index.tsx';
import { BottomAdornment, LengthCounter, StyledTextarea, TextareaWrapper } from './styled.ts';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  isLoading?: boolean;
  success?: boolean;
  warning?: boolean;
  error?: boolean;
  dataTestId?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { isLoading, success, warning, error, maxLength, value, className, dataTestId, ...props },
  ref,
) {
  const { colors } = useTheme();
  const inputDisabled = props.disabled || props.readOnly;
  const valueLength = typeof value === 'string' ? value.length : 0;
  const hasAdornment = isLoading || !!maxLength;

  return (
    <TextareaWrapper
      $inputDisabled={inputDisabled}
      $success={success}
      $warning={warning}
      $error={error}
      className={className}
      data-testid={dataTestId}>
      <StyledTextarea ref={ref} $hasAdornment={hasAdornment} maxLength={maxLength} value={value} {...props} />
      {hasAdornment && (
        <BottomAdornment>
          {isLoading && <Loader fill={colors.grey100_1} background='transparent' />}
          {!!maxLength && (
            <LengthCounter>
              {valueLength}
              {'/'}
              {maxLength}
            </LengthCounter>
          )}
        </BottomAdornment>
      )}
    </TextareaWrapper>
  );
});

Textarea.displayName = 'Textarea';
